import type { AnalysisReport } from '@rb/ui'

// ─────────────────────────────────────────────────────────────────────────────
// SchafferHolz Energiebericht 2025 — faithful transcription into the block model.
//
// All figures reconcile to the headline KPIs:
//   Gesamtverbrauch 2.697 MWh · Netzbezug 2.674 MWh · Einspeisung 382 MWh
//   Jahreshöchstlast 1.176 kW · Ø-Last 308 kW · Lastfaktor 26,2 %
//   (308 kW / 1.176 kW = 26,2 %;  2.697 MWh / 8.760 h = 308 kW)
//   Selbstverbrauch PV = 2.697 − 2.674 = 23 MWh; PV-Erzeugung = 23 + 382 = 405 MWh.
// Screen rendering only — the PDF report (ProjectReport.tsx) still uses the old
// schema in data/stubAnalysis.ts.
// ─────────────────────────────────────────────────────────────────────────────

const MONTHS = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez']

// Monthly Verbrauch (MWh) — Σ = 2.697
const VERBRAUCH = [280, 255, 240, 210, 195, 180, 175, 185, 205, 230, 255, 287]
// Monthly Einspeisung (MWh) — Σ = 382
const EINSPEISUNG = [8, 14, 26, 38, 50, 56, 58, 48, 36, 24, 12, 12]
// Monthly Netzbezug (MWh) — Σ = 2.674 (Verbrauch − PV-Selbstverbrauch)
const BEZUG = [279.5, 254, 238.5, 208, 192, 176.5, 171.5, 182, 203, 228.5, 254, 286.5]
// Monthly Max-Last (kW) — annual peak 1.176 (Dez)
const MAX_KW = [1120, 1095, 980, 870, 760, 720, 690, 740, 850, 960, 1080, 1176]
// Monthly Ø-Last (kW) = Verbrauch[MWh]·1000 / Stunden im Monat
const AVG_KW = [376, 379, 323, 292, 262, 250, 235, 249, 285, 309, 354, 386]
// Monthly PLAN (MWh) — Beschaffungsbudget 2025, Σ = 2.670
const PLAN = [270, 250, 245, 215, 200, 185, 180, 180, 200, 225, 245, 275]
// Abweichung IST vs PLAN (%) per Monat
const ABW = ['+3,7 %', '+2,0 %', '-2,0 %', '-2,3 %', '-2,5 %', '-2,7 %', '-2,8 %', '+2,8 %', '+2,5 %', '+2,2 %', '+4,1 %', '+4,4 %']

// 24-h Lastprofil (kW) — repräsentative Mittelwerte
const WERKTAG = [200, 190, 185, 185, 190, 230, 330, 480, 640, 720, 760, 780, 740, 760, 770, 740, 680, 560, 430, 350, 300, 270, 240, 210]
const WOCHENENDE = [180, 175, 170, 168, 170, 180, 200, 230, 280, 330, 380, 410, 420, 410, 400, 380, 350, 320, 300, 280, 260, 240, 220, 195]

// 24×12 Heatmap (kW): Stunde × Monat = Werktagsprofil skaliert auf den Monats-Ø.
const HOURS = Array.from({ length: 24 }, (_, h) => `${String(h).padStart(2, '0')}:00`)
const HEATMAP_VALUES: number[][] = WERKTAG.map((base) =>
  AVG_KW.map((avg) => Math.round((base * avg) / 308))
)

// Jahresdauerlinie (sortierte, abgetastete Lastkurve): x = % der Jahresstunden.
const DURATION_CURVE: [number, number][] = [
  [0, 1176], [2, 1080], [4, 980], [6, 900], [8, 840], [10, 790],
  [15, 710], [20, 650], [25, 600], [30, 560], [35, 520], [40, 485],
  [45, 455], [50, 425], [55, 400], [60, 375], [65, 350], [70, 328],
  [75, 305], [80, 282], [85, 258], [90, 232], [95, 200], [98, 172], [100, 148],
]

export const SCHAFFERHOLZ_REPORT: AnalysisReport = {
  cover: {
    subtitle: '15-Minuten-Lastganganalyse | EDM-Datenexport',
    period: '01. Jänner 2025 – 31. Dezember 2025',
  },
  analyse: [
    // 1 ── Überblick ────────────────────────────────────────────────────────
    {
      section_title: 'Überblick',
      blocks: [
        {
          type: 'kpi_grid',
          items: [
            { value: '2.697', label: 'Gesamtverbrauch', unit: 'MWh' },
            { value: '2.674', label: 'Netzbezug', unit: 'MWh' },
            { value: '382', label: 'Einspeisung', unit: 'MWh' },
            { value: '1.176', label: 'Jahreshöchstlast', unit: 'kW' },
            { value: '308', label: 'Ø-Last', unit: 'kW' },
            { value: '26,2', label: 'Lastfaktor', unit: '%' },
          ],
        },
        {
          type: 'paragraph',
          text:
            'Der Lastgang des Betriebs **SchafferHolz** (Holzverarbeitung) zeigt ein typisches ' +
            'industrielles Profil mit ausgeprägter Werktags-Tagschicht. Bei einem Gesamtverbrauch ' +
            'von **2.697 MWh** und einer Jahreshöchstlast von **1.176 kW** ergibt sich ein ' +
            'Lastfaktor von nur **26,2 %** — ein klarer Hinweis auf erhebliches ' +
            'Lastspitzenkappungspotenzial.',
        },
        {
          type: 'paragraph',
          text:
            'Die Dach-PV-Anlage (120 kWp, Halle 3) erzeugte 2025 rund **405 MWh**. Davon wurden nur ' +
            '**23 MWh** direkt selbst verbraucht, **382 MWh** wurden ins Netz eingespeist — der ' +
            'Eigenverbrauchsanteil ist mit ~6 % sehr niedrig. Datenbasis: 15-Minuten-Messwerte ' +
            '(35.040 Messpunkte), Planwerte aus dem Energiebeschaffungsvertrag 2025.',
        },
      ],
    },

    // 2 ── Jahresverbrauch ────────────────────────────────────────────────────
    {
      section_title: 'Jahresverbrauch',
      blocks: [
        {
          type: 'bar_chart',
          title: 'Monatlicher Verbrauch und Einspeisung',
          x_label: 'Monat',
          y_label: 'MWh',
          series: [
            { name: 'Verbrauch', points: MONTHS.map((m, i) => ({ x: m, y: VERBRAUCH[i] })) },
            { name: 'Einspeisung', points: MONTHS.map((m, i) => ({ x: m, y: EINSPEISUNG[i] })) },
          ],
        },
        {
          type: 'paragraph',
          text:
            'Der Verbrauch folgt einem deutlichen **Wintermaximum**: Dezember (287 MWh) und Januar ' +
            '(280 MWh) liegen rund 60 % über den Sommermonaten Juni/Juli (180/175 MWh). Treiber ' +
            'sind Hallenheizung, Trocknungsanlagen und längere Beleuchtungszeiten in der dunklen ' +
            'Jahreszeit.',
        },
        {
          type: 'paragraph',
          text:
            'Die **Einspeisung** verläuft gegenläufig und erreicht ihr Maximum im Sommer ' +
            '(Juni/Juli ~57 MWh/Monat). Eine zeitliche Verschiebung von Lasten in die ' +
            'Mittagsstunden könnte den Eigenverbrauch der PV-Erzeugung spürbar erhöhen.',
        },
      ],
    },

    // 3 ── Monatliche Detailanalyse ───────────────────────────────────────────
    {
      section_title: 'Monatliche Detailanalyse',
      blocks: [
        {
          type: 'table',
          title: 'Monatswerte 2025',
          columns: ['Monat', 'Verbrauch (MWh)', 'Bezug (MWh)', 'Einspeisung (MWh)', 'Max (kW)', 'Ø (kW)', 'PLAN (MWh)', 'Abw.'],
          rows: [
            ...MONTHS.map((m, i) => [
              m,
              VERBRAUCH[i],
              String(BEZUG[i]).replace('.', ','),
              EINSPEISUNG[i],
              MAX_KW[i],
              AVG_KW[i],
              PLAN[i],
              ABW[i],
            ] as (string | number)[]),
            ['Gesamt', 2697, '2.674', 382, 1176, 308, 2670, '+1,0 %'],
          ],
        },
        {
          type: 'paragraph',
          text:
            '**Saisonale Muster:** Die fünf verbrauchsstärksten Monate (Nov–Mär) decken rund 49 % ' +
            'des Jahresverbrauchs ab. Die höchste Ø-Last tritt im Dezember (386 kW) auf, das ' +
            'Minimum im Juli (235 kW). Die Monats-Höchstlasten konzentrieren sich auf das Winter­halb­jahr; ' +
            'in den Sommermonaten bleibt die Spitze trotz laufender Produktion deutlich unter 800 kW.',
        },
      ],
    },

    // 4 ── PLAN vs IST ─────────────────────────────────────────────────────────
    {
      section_title: 'PLAN vs IST',
      blocks: [
        {
          type: 'bar_chart',
          title: 'IST-Verbrauch gegen PLAN-Budget',
          x_label: 'Monat',
          y_label: 'MWh',
          series: [
            { name: 'IST', points: MONTHS.map((m, i) => ({ x: m, y: VERBRAUCH[i] })) },
            { name: 'PLAN', points: MONTHS.map((m, i) => ({ x: m, y: PLAN[i] })) },
          ],
        },
        {
          type: 'table',
          title: 'Abweichung IST vs PLAN',
          columns: ['Monat', 'IST (MWh)', 'PLAN (MWh)', 'Abw.'],
          rows: [
            ...MONTHS.map((m, i) => [m, VERBRAUCH[i], PLAN[i], ABW[i]] as (string | number)[]),
            ['Gesamt', 2697, 2670, '+1,0 %'],
          ],
        },
        {
          type: 'paragraph',
          text:
            '**Bewertung:** Über das Gesamtjahr liegt der IST-Verbrauch mit **+1,0 %** nahezu ' +
            'punktgenau im Budget — die Beschaffungsplanung 2025 war treffsicher. Auffällig sind die ' +
            'Wintermonate **November (+4,1 %)** und **Dezember (+4,4 %)**, in denen die kalte ' +
            'Witterung über Plan lag.',
        },
        {
          type: 'callout',
          title: 'Beschaffungsempfehlung',
          variant: 'info',
          text:
            'Die positiven Winterabweichungen werden durch leichte Unterschreitungen im Frühjahr/Sommer ' +
            'kompensiert. Für 2026 empfiehlt sich eine geringfügige Anhebung des Q4-Budgets bei ' +
            'gleichbleibender Jahresmenge.',
        },
      ],
    },

    // 5 ── Lastprofil ──────────────────────────────────────────────────────────
    {
      section_title: 'Lastprofil',
      blocks: [
        {
          type: 'line_chart',
          title: 'Tageslastprofil — Werktag vs Wochenende',
          x_label: 'Stunde',
          y_label: 'kW',
          series: [
            { name: 'Werktag', points: WERKTAG.map((y, h) => ({ x: h, y })) },
            { name: 'Wochenende', points: WOCHENENDE.map((y, h) => ({ x: h, y })) },
          ],
        },
        {
          type: 'paragraph',
          text:
            '- **Werktags** steigt die Last ab 05:00 Uhr steil an und erreicht zwischen 10:00 und ' +
            '11:00 Uhr ihr Plateau (~780 kW).\n' +
            '- Eine **Mittagsdelle** ist nur schwach ausgeprägt; die Produktion läuft durchgehend.\n' +
            '- Nach Schichtende fällt die Last ab 17:00 Uhr deutlich, die Nacht-Grundlast liegt bei ' +
            '~185 kW.\n' +
            '- Am **Wochenende** bleibt die Last flach (~170–420 kW) — Grundlast plus reduzierter ' +
            'Wochenendbetrieb.',
        },
        {
          type: 'table',
          title: 'Wochenprofil',
          columns: ['Wochentag', 'Ø Last (kW)', 'Max Last (kW)', 'Anteil'],
          rows: [
            ['Montag', 410, 1080, '16 %'],
            ['Dienstag', 420, 1120, '16 %'],
            ['Mittwoch', 415, 1100, '16 %'],
            ['Donnerstag', 418, 1150, '16 %'],
            ['Freitag', 400, 1176, '15 %'],
            ['Samstag', 300, 720, '11 %'],
            ['Sonntag', 250, 560, '10 %'],
          ],
        },
      ],
    },

    // 6 ── Lastheatmap ─────────────────────────────────────────────────────────
    {
      section_title: 'Lastheatmap',
      blocks: [
        {
          type: 'heatmap',
          title: 'Durchschnittliche Last nach Stunde und Monat',
          unit: 'kW',
          x_labels: MONTHS,
          y_labels: HOURS,
          values: HEATMAP_VALUES,
        },
        {
          type: 'paragraph',
          text:
            'Die Heatmap verdichtet das Jahr auf das Muster **Stunde × Monat**. Deutlich erkennbar ist ' +
            'der „heiße" Block der Werktags-Tagschicht (08:00–16:00 Uhr) in den Wintermonaten, in dem ' +
            'die höchsten Durchschnittslasten auftreten. Die kühlen Nacht- und Sommerbereiche zeigen, ' +
            'wo Lastverlagerung am wenigsten kostet.',
        },
      ],
    },

    // 7 ── Lastspitzen ─────────────────────────────────────────────────────────
    {
      section_title: 'Lastspitzen',
      blocks: [
        {
          type: 'table',
          title: 'Top-10 Lastspitzen des Jahres',
          columns: ['Rang', 'Datum', 'Uhrzeit', 'Leistung (kW)', 'Monat'],
          rows: [
            [1, '18.12.2025', '17:30', 1176, 'Dez'],
            [2, '11.12.2025', '17:15', 1158, 'Dez'],
            [3, '04.12.2025', '17:45', 1142, 'Dez'],
            [4, '22.01.2025', '17:30', 1120, 'Jan'],
            [5, '12.02.2025', '17:15', 1095, 'Feb'],
            [6, '19.11.2025', '17:30', 1080, 'Nov'],
            [7, '27.11.2025', '17:45', 1072, 'Nov'],
            [8, '15.01.2025', '17:00', 1064, 'Jan'],
            [9, '06.02.2025', '17:30', 1051, 'Feb'],
            [10, '13.12.2025', '16:45', 1043, 'Dez'],
          ],
        },
        {
          type: 'table',
          title: 'Monatliche Höchstlasten',
          columns: ['Monat', 'Max (kW)', 'Datum'],
          rows: MONTHS.map((m, i) => [m, MAX_KW[i], `${['22', '12', '14', '09', '21', '17', '22', '19', '16', '14', '19', '18'][i]}.${String(i + 1).padStart(2, '0')}.2025`] as (string | number)[]),
        },
        {
          type: 'paragraph',
          text:
            'Alle zehn Jahreshöchstlasten liegen im **Winterhalbjahr (Nov–Feb)** und treten ' +
            'durchgängig am **späten Nachmittag (16:45–17:45 Uhr)** auf — also beim Zusammenfallen von ' +
            'Produktionslast, Heizung und einsetzender Beleuchtung. Die zehn höchsten Viertelstunden ' +
            'des Jahres bestimmen das Leistungsentgelt überproportional.',
        },
        {
          type: 'callout',
          title: 'Kappungspotenzial',
          variant: 'warning',
          priority: 'Wirkung: Hoch',
          text:
            'Würde die Spitze von 1.176 kW auf 700 kW begrenzt, sänke das abgerechnete Leistungsentgelt ' +
            'erheblich. Die Spitzen sind eng auf wenige Stunden konzentriert — ideale Voraussetzung für ' +
            'einen Speicher oder gezielte Lastverlagerung.',
        },
      ],
    },

    // 8 ── Jahresdauerlinie ────────────────────────────────────────────────────
    {
      section_title: 'Jahresdauerlinie',
      blocks: [
        {
          type: 'line_chart',
          title: 'Geordnete Jahresdauerlinie',
          x_label: '% der Jahresstunden',
          y_label: 'kW',
          series: [
            { name: 'Last', points: DURATION_CURVE.map(([x, y]) => ({ x, y })) },
          ],
        },
        {
          type: 'paragraph',
          text:
            'Die Jahresdauerlinie ordnet alle Viertelstunden des Jahres nach Leistung absteigend. Der ' +
            'steile Abfall am linken Rand bestätigt: Lasten über **900 kW** treten nur an wenigen ' +
            'Prozent der Jahresstunden auf, während die **Grundlast bei ~150 kW** ganzjährig anliegt.',
        },
        {
          type: 'paragraph',
          text:
            'Die Ø-Last von **308 kW** wird etwa zur Jahresmitte unterschritten. Die große Fläche ' +
            'zwischen Spitze und Durchschnitt ist genau das Volumen, das durch Lastmanagement und ' +
            'Speichereinsatz wirtschaftlich erschlossen werden kann.',
        },
      ],
    },

    // 9 ── Handlungsempfehlungen ───────────────────────────────────────────────
    {
      section_title: 'Handlungsempfehlungen',
      blocks: [
        {
          type: 'callout',
          title: 'Lastspitzenkappung (Peak Shaving)',
          variant: 'warning',
          priority: 'Priorität: Hoch',
          text:
            'Die Jahreshöchstlast von **1.176 kW** wird von wenigen winterlichen Nachmittagsspitzen ' +
            'bestimmt. Ein Batteriespeicher bzw. virtueller Speicher (Lastverlagerung) kann diese ' +
            'gezielt kappen und das Leistungsentgelt deutlich senken. → Detailbewertung in den ' +
            'Bereichen *Vorschlag* und *ROI*.',
        },
        {
          type: 'callout',
          title: 'Lastverschiebung Werktagsmorgen',
          variant: 'info',
          priority: 'Priorität: Mittel',
          text:
            'Der steile Lastanstieg ab 05:00 Uhr (Anlauf von Kompressoren und Trocknung) lässt sich ' +
            'durch gestaffeltes Zuschalten glätten — ohne Investition, allein über die Steuerung.',
        },
        {
          type: 'callout',
          title: 'PV-Eigenverbrauch erhöhen',
          variant: 'success',
          priority: 'Priorität: Mittel',
          text:
            'Von 405 MWh PV-Erzeugung werden nur 23 MWh selbst genutzt. Durch Verlagerung flexibler ' +
            'Lasten in die Mittagsstunden und/oder Speicherung lässt sich der Eigenverbrauch ' +
            'vervielfachen und der Netzbezug reduzieren.',
        },
        {
          type: 'callout',
          title: 'Kontinuierliches Energiemonitoring',
          variant: 'info',
          priority: 'Priorität: Niedrig',
          text:
            'Ein dauerhaftes Monitoring der Viertelstundenwerte sichert die erzielten Einsparungen ab ' +
            'und macht Lastspitzen frühzeitig sichtbar, bevor sie das Leistungsentgelt treiben.',
        },
      ],
    },
  ],

  // Platzhalter — werden in einem späteren Schritt ausgearbeitet.
  vorschlag: [
    {
      section_title: 'Maßnahmenübersicht',
      blocks: [
        { type: 'heading', text: 'Empfohlene Maßnahmen' },
        {
          type: 'paragraph',
          text:
            'Auf Basis der Analyse wird vorrangig die **Lastspitzenkappung** empfohlen. Die konkrete ' +
            'Dimensionierung von Speicher und virtuellem Speicher wird hier in Kürze ausgearbeitet.',
        },
        {
          type: 'callout',
          title: 'In Bearbeitung',
          variant: 'info',
          text: 'Die detaillierten Vorschläge werden derzeit erstellt.',
        },
      ],
    },
  ],

  roi: [
    {
      section_title: 'Wirtschaftlichkeit',
      blocks: [
        { type: 'heading', text: 'Wirtschaftlichkeitsbetrachtung' },
        {
          type: 'paragraph',
          text:
            'Die Wirtschaftlichkeitsrechnung (Einsparung, Investition, Amortisation je Szenario) folgt, ' +
            'sobald die Maßnahmen im Bereich *Vorschlag* finalisiert sind.',
        },
        {
          type: 'kpi_grid',
          items: [
            { value: '~470', label: 'Kappbare Spitze (Ziel)', unit: 'kW' },
            { value: 'in Arbeit', label: 'Einsparung / Jahr' },
            { value: 'in Arbeit', label: 'Amortisation' },
          ],
        },
      ],
    },
  ],

  // Leer — testet den „noch nicht verfügbar"-Zustand der Karte.
  finanzierung: [],
}
